import { useUserStore } from "@hooks/useUserStore"
import {
	ensureInsightsApiCsrfToken,
	ensureMainApiCsrfToken,
	initializeCsrf
} from "@services/csrfService"
import { logout as forgeRockLogout } from "@services/forgeRockAuthService"
import { StorageKeys } from "@utils/user-config"
import axios, { AxiosError, AxiosInstance } from "axios"
import secureLocalStorage from "react-secure-storage"
import { LoginError } from "../models/error/login-error"
import { UserProfile } from "../models/userProfile.model"
import {
	APP_URL,
	BASE_URL,
	COMPLIANCE_COMPLETE_MODAL_SHOWN_KEY,
	COMPLIANCE_INTENDED_PATH_STORAGE_KEY,
	COMPLIANCE_MODAL_SHOWN_KEY,
	PARTNER_INSIGHTS_API,
	SOFT_COMPLIANCE_CHECK_MODAL_SHOWN_KEY,
	USE_FORGEROCK,
	VITE_AUTH_ENCRYPTION_KEY
} from "../utils/constants.util"

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"])

export interface LoginResponse {
	access_token: string
	refresh_token: string
	id_token: string
	user: UserProfile
}

export interface RemoteAccessResponse {
	remote_token: string
}

interface RemoteAccess {
	remoteAccessToken: string
}

interface RefreshTokenResponse {
	access_token: string
	refresh_token: string
	id_token: string
	expires_in: number
	token_type: string
}

interface QAModeResponse {
	data: boolean
}

const setupInterceptors = (
	instance: AxiosInstance,
	ensureCsrfToken: () => Promise<string | null>
): AxiosInstance => {
	instance.interceptors.request.use(
		async (config) => {
			const token = getAuthToken()
			const idToken = getIdToken()
			if (token) {
				config.headers.Authorization = `Bearer ${token}`
			}

			if (idToken) {
				config.headers["X-Id-Token"] = idToken
			}

			const activeAgencyId = secureLocalStorage.getItem(
				StorageKeys.ACTIVE_AGENCY_ID
			)
			if (activeAgencyId) {
				config.headers["x-active-agency"] = String(activeAgencyId)
			}
			if (token && MUTATING_METHODS.has(config.method?.toLowerCase() ?? "")) {
				const csrfToken = await ensureCsrfToken()
				if (csrfToken) {
					config.headers["X-CSRF-Token"] = csrfToken
				}
			}

			return config
		},
		(error) => {
			return Promise.reject(error)
		}
	)

	instance.interceptors.response.use(
		(response) => {
			if (response.config.url !== "/auth/logout") {
				secureLocalStorage.setItem(
					StorageKeys.LAST_ACTIVE_TIME,
					Date.now().toString()
				)
			}
			return response
		},
		async (error: AxiosError) => {
			const isCsrfError =
				error.response?.status === 403 &&
				(error.response?.data as { message?: { message?: string } })?.message
					?.message === "Invalid CSRF token"

			if (
				isCsrfError &&
				error.config &&
				!(error.config as unknown as Record<string, unknown>)._csrfRetried
			) {
				;(error.config as unknown as Record<string, unknown>)._csrfRetried =
					true
				await initializeCsrf()
				return instance(error.config)
			}

			// Skip if we're already on the logout endpoint to prevent infinite loop
			if (
				error.response?.status === 401 &&
				error.config?.url !== "/auth/logout" &&
				error.config?.url !== "/auth/login"
			) {
				try {
					// Get tokens BEFORE clearing session to avoid null token issues
					const idToken = getIdToken()

					// Clear local session
					clearSession()

					// Handle ForgeRock logout if enabled
					if (USE_FORGEROCK) {
						try {
							const oauthService = await import("@services/oauthService")

							// Clear ForgeRock SDK tokens
							await forgeRockLogout()

							// If we have an ID token, perform OAuth logout which handles session termination
							if (idToken) {
								// Perform OAuth logout which redirects to ForgeRock's end session endpoint
								await oauthService.default.logout(idToken, `${APP_URL}/login`)
								return Promise.reject(error) // Don't continue after redirect
							}
						} catch (_logoutError) {
							// Fall through to regular logout handling
						}
					}

					// Perform backend logout call only for legacy auth (non-ForgeRock)
					else {
						try {
							await logout()
						} catch (_logoutError) {
							// Continue with redirect even if backend logout fails
						}
					}

					// Redirect to login page
					window.location.href = "/login"
				} catch (_logoutError) {
					// Fallback: clear session and redirect
					clearSession()
					window.location.href = "/login"
				}

				// Don't continue processing the original request
				return Promise.reject(error)
			}

			return Promise.reject(error)
		}
	)

	return instance
}

const createBaseInstance = (baseURL: string): AxiosInstance => {
	return axios.create({
		baseURL,
		withCredentials: true,
		headers: {
			"Content-Type": "application/json",
			"Cache-Control": "max-age=0, no-cache, no-store"
		}
	})
}

export const api = setupInterceptors(
	createBaseInstance(BASE_URL),
	ensureMainApiCsrfToken
)
export const partnerInsightsApi = setupInterceptors(
	createBaseInstance(PARTNER_INSIGHTS_API),
	ensureInsightsApiCsrfToken
)

export const getAuthToken = (): string => {
	const token = secureLocalStorage.getItem(StorageKeys.USER_TOKEN)
	return token as string
}

const getIdToken = (): string => {
	const token = secureLocalStorage.getItem(StorageKeys.USER_ID_TOKEN)
	return token as string
}

const getRefreshToken = (): string => {
	return secureLocalStorage.getItem(StorageKeys.REFRESH_TOKEN) as string
}

const setAuthToken = (
	token: string,
	idToken?: string,
	refreshToken?: string
): void => {
	secureLocalStorage.setItem(StorageKeys.USER_TOKEN, token)
	if (idToken) {
		secureLocalStorage.setItem(StorageKeys.USER_ID_TOKEN, idToken)
	}
	if (refreshToken) {
		secureLocalStorage.setItem(StorageKeys.REFRESH_TOKEN, refreshToken)
	}
	secureLocalStorage.setItem(
		StorageKeys.USER_LOGIN_TIME,
		JSON.stringify(Date.now())
	)
	secureLocalStorage.setItem(
		StorageKeys.LAST_ACTIVE_TIME,
		Date.now().toString()
	)
}

const clearSession = (): void => {
	secureLocalStorage.removeItem(StorageKeys.USER_TOKEN)
	secureLocalStorage.removeItem(StorageKeys.USER_ID_TOKEN)
	secureLocalStorage.removeItem(StorageKeys.REFRESH_TOKEN)
	secureLocalStorage.removeItem(StorageKeys.USER_PROFILE)
	secureLocalStorage.removeItem(StorageKeys.USER_LOGIN_TIME)
	secureLocalStorage.removeItem(StorageKeys.LAST_ACTIVE_TIME)
	secureLocalStorage.removeItem(StorageKeys.HAS_USER_NAVIGATED_TO_LEGACY)
	secureLocalStorage.removeItem(
		StorageKeys.LEGACY_AUTHOMATIC_REDIRECT_ALREADY_DONE
	)
	secureLocalStorage.removeItem(StorageKeys.ACTIVE_AGENCY_ID)
	sessionStorage.removeItem(COMPLIANCE_MODAL_SHOWN_KEY)
	sessionStorage.removeItem(COMPLIANCE_INTENDED_PATH_STORAGE_KEY)
	sessionStorage.removeItem(COMPLIANCE_COMPLETE_MODAL_SHOWN_KEY)
	sessionStorage.removeItem(SOFT_COMPLIANCE_CHECK_MODAL_SHOWN_KEY)
}

const getLoginDate = (): string | null => {
	return secureLocalStorage.getItem(StorageKeys.USER_LOGIN_TIME) as string
}

const getTokenExpiration = (): string | null => {
	return new Date(Date.now() + 1 * 60 * 1000).toISOString()
}

export const decodeJWTToken = (
	token: string
): Record<string, unknown> | null => {
	try {
		if (!token) return null

		const parts = token.split(".")

		// Handle JWE tokens (5 parts) - these cannot be decoded client-side
		if (parts.length === 5) {
			return null
		}

		// Handle JWT tokens (3 parts) - for development environments
		if (parts.length !== 3) {
			return null
		}

		const base64Url = parts[1]
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
				.join("")
		)
		return JSON.parse(jsonPayload)
	} catch (_error) {
		return null
	}
}

const extractUsernameFromToken = (
	tokenPayload: Record<string, unknown> | null
): string | undefined => {
	if (!tokenPayload) return undefined

	if (USE_FORGEROCK) {
		return tokenPayload.subname as string
	}
	return tokenPayload.username as string
}

export const login = async (
	username: string,
	password: string
): Promise<{
	token: string
	refresh_token: string
	id_token: string
	userProfile: UserProfile
}> => {
	try {
		// Always reset to default agency before the login call (spec requirement)
		secureLocalStorage.removeItem(StorageKeys.ACTIVE_AGENCY_ID)

		const encryptedCredentials = await encryptCredentials({
			username,
			password
		})
		const { data } = await api.post<{ data: LoginResponse }>("/auth/login", {
			encryptedCredentials
		})

		const {
			access_token: token,
			refresh_token,
			id_token,
			user: userProfile
		} = data.data

		const decodedToken = decodeJWTToken(token)

		// For JWE tokens (staging/production), skip username validation
		// as we cannot decode them client-side
		if (decodedToken !== null) {
			const tokenUsername = extractUsernameFromToken(decodedToken)

			if (
				!tokenUsername ||
				(USE_FORGEROCK &&
					tokenUsername?.toLowerCase() !== username?.toLowerCase())
			) {
				throw new LoginError(
					"An error occurred while logging in. Please try again.",
					"INVALID_TOKEN",
					0
				)
			}
		} else {
			// JWE token - trust server-side validation
		}

		setAuthToken(token, id_token, refresh_token)

		return { token, refresh_token, id_token, userProfile }
	} catch (error) {
		if (error instanceof AxiosError) {
			const { message, errorCode, remainingAttempts } =
				error.response?.data?.message || {}

			if (!errorCode) {
				throw new LoginError(
					"Service unavailable, please try again later",
					errorCode,
					remainingAttempts
				)
			}

			throw new LoginError(message, errorCode, remainingAttempts)
		}
		throw error
	}
}

const preLoginCheck = async (
	username: string
): Promise<{ message: string; success: boolean }> => {
	try {
		const { data } = await api.post<{
			data: { message: string; success: boolean }
		}>("/auth/pre-login", {
			username
		})
		return data.data
	} catch (error) {
		if (error instanceof AxiosError) {
			const { message, errorCode } = error.response?.data?.message || {}

			if (!errorCode) {
				throw new LoginError(
					"Service unavailable, please try again later",
					errorCode,
					0
				)
			}

			throw new LoginError(message, errorCode, 0)
		}
		throw error
	}
}

const buildUserProfile = async (): Promise<UserProfile> => {
	try {
		const { data } = await api.post<{ data: UserProfile }>(
			"/auth/build-profile",
			{ headers: { Authorization: `Bearer ${getAuthToken()}` } }
		)

		return data.data
	} catch (_error) {
		throw new Error("Error building user profile")
	}
}

const encryptCredentials = async (data: {
	username: string
	password: string
}): Promise<string> => {
	const ENCRYPTION_KEY = VITE_AUTH_ENCRYPTION_KEY

	const jsonString = JSON.stringify(data)

	const encoder = new TextEncoder()
	const dataBuffer = encoder.encode(jsonString)

	const iv = window.crypto.getRandomValues(new Uint8Array(12))

	const key = await window.crypto.subtle.importKey(
		"raw",
		encoder.encode(ENCRYPTION_KEY),
		{ name: "AES-GCM" },
		false,
		["encrypt"]
	)

	const encryptedContent = await window.crypto.subtle.encrypt(
		{
			name: "AES-GCM",
			iv: iv
		},
		key,
		dataBuffer
	)

	const encryptedArray = new Uint8Array([
		...iv,
		...new Uint8Array(encryptedContent)
	])
	return btoa(String.fromCharCode(...encryptedArray))
}

export const logout = async (): Promise<void> => {
	try {
		await api.delete<{ data: boolean }>("/auth/logout", {
			headers: { Authorization: `Bearer ${getAuthToken()}` }
		})
	} catch (_error) {
		throw new Error("Error logout user")
	}
}

export const checkSession = (): boolean => {
	return !!secureLocalStorage.getItem(StorageKeys.USER_TOKEN)
}

export const remoteCPAccess = async (
	target: string
): Promise<RemoteAccessResponse> => {
	try {
		const { data } = await api.get<{ data: RemoteAccessResponse }>(
			`/auth/remoteCPAccess?target=${target}`
		)
		return data.data
	} catch (_error) {
		throw new Error("Error accessing remotely")
	}
}

export const webserviceAccess = async (
	applicationId: string
): Promise<RemoteAccess> => {
	try {
		// Prepare request config
		const config = {
			params: { applicationId },
			headers: {} as Record<string, string>
		}

		if (USE_FORGEROCK) {
			const idToken = getIdToken()
			if (!idToken) {
				throw new Error("ID token is missing. Please log in again.")
			}
			config.headers["X-Id-Token"] = idToken
		}

		const { data } = await api.get<{ data: RemoteAccess }>("/redirect", config)
		return data.data
	} catch (_error) {
		if (_error instanceof Error) {
			throw _error
		}
		throw new Error("Error accessing web service")
	}
}

export const fetchProfile = async (): Promise<UserProfile> => {
	try {
		const { data } = await api.get<{ data: UserProfile }>("/userProfile")

		return data.data
	} catch (_error) {
		throw new Error("Error fetching user profile")
	}
}

export const changeLocationData = (
	userProfile: UserProfile | null,
	lat?: string,
	long?: string
): void => {
	const { setUserProfile } = useUserStore()

	if (userProfile) {
		const locationLat =
			lat ??
			(userProfile.locationData?.find((item) => item.key === "lat")?.value ||
				"")
		const locationLong =
			long ??
			(userProfile.locationData?.find((item) => item.key === "long")?.value ||
				"")

		const updatedProfile = {
			...userProfile,
			lat: locationLat ?? "",
			long: locationLong ?? ""
		}

		setUserProfile(updatedProfile)
	}
}

export const moveLocationData = (profile: UserProfile) => {
	const locationLat =
		profile?.locationData?.find((item) => item.key === "lat")?.value || ""
	const locationLong =
		profile?.locationData?.find((item) => item.key === "long")?.value || ""
	return {
		...profile,
		lat: locationLat,
		long: locationLong
	}
}

interface ValidateLegacyTokenResponse {
	isValid: boolean
	payload: {
		"creation-date-time": string
		username: string
	}
	access_token: string
	id_token: string
}

export const validateLegacyToken = async (
	token: string
): Promise<{ access_token: string; id_token: string } | null> => {
	try {
		const { data } = await api.get<{
			data: ValidateLegacyTokenResponse
		}>("/auth/validate-legacy-token", {
			headers: {
				"Cp-Token": token
			}
		})

		if (data.data.isValid) {
			return {
				access_token: data.data.access_token,
				id_token: data.data.id_token
			}
		}

		return null
	} catch (error) {
		throw new Error(`Error validating legacy token ${error}`)
	}
}

const refreshAccessToken = async (): Promise<RefreshTokenResponse> => {
	const refreshToken = getRefreshToken()
	if (!refreshToken) {
		throw new Error("No refresh token available")
	}

	const response = await axios.post<{ data: RefreshTokenResponse }>(
		`${BASE_URL}/auth/refresh-token`,
		{ refresh_token: refreshToken }
	)
	const { data } = response
	return data.data
}

const getQAModeStatus = async (): Promise<boolean> => {
	try {
		const { data } = await api.get<QAModeResponse>("/auth/qa-mode")
		return data.data
	} catch (_error) {
		return false
	}
}

export const AuthService = {
	login,
	preLoginCheck,
	buildUserProfile,
	logout,
	checkSession,
	remoteCPAccess,
	webserviceAccess,
	getAuthToken,
	getIdToken,
	setAuthToken,
	fetchProfile,
	clearSession,
	getLoginDate,
	getTokenExpiration,
	validateLegacyToken,
	refreshAccessToken,
	getRefreshToken,
	decodeJWTToken,
	extractUsernameFromToken,
	getQAModeStatus
}
